// #region Imports

/* Telestream */
import { AddTimecodes, SubtractTimecodes, ToTimeCode } from '@user-interface/timecode-utilities';
import { TimecodeService } from '@user-interface/glim-player';
import { GLIM_TIMECODE_SOURCE, NPT_TIMECODE_SOURCE } from '@/utils/constants';

// #endregion Imports

// #region Constants

/**
 * When fetching the timecode for a particular offset in seconds, a binary search
 * will be performed in a list of cues. Each cue has a start and end time, however,
 * the end time of a particular cue and the start time of the next cue are the
 * same. 
 * 
 * This means that, depending on how the binary search pans out (usually relative
 * to how many cues are in the search list), you will get different resultss for
 * the same time, if that time falls on a cue boundary.
 * 
 * By adding this fudge factor below, this problem will be somewhat alleviated,
 * especially for round-number times (e.g. 2 seconds exactly).
 */
const EPSILON = 0.0001;

// #endregion Constants

/*
 * TODO: These operations currently use the glim-provided timecode since there
 * is no way to change the selection yet.
 */

/**
 * Gets the first timecode contained in the special metadata tracks of the specified
 * video element.
 *
 * @param {HTMLVideoElement} element The element from which to extract the timecode.
 * @param {string} sourceName The name of the source to get the firts timecode from.
 * @returns The first timecode contained in the specified element.
 */
export function getFirstTimecode(element, sourceName) {
  const sources = getTimecodeSources(element, 0);
  if (!sources) {
    return estimateFirstTimecode(element, sourceName);
  }

  let source = sources.find(s => s.track === sourceName);
  if (!source) {
    source = sources[sources.length - 1];
  }

  if (sourceName === NPT_TIMECODE_SOURCE.name) {
    return nptToTimecode(source.value, ':');
  }

  return source.value;
}

/**
 * Provides a rough estimate of the first timecode of the media contained in the
 * specified element. Note that there are many ways in which this estimation can
 * be incorrect(the media has timecode discontinuities or has a looping timecode 
 * track) or outright fail (no content has been buffered so there is no current
 * timecode to guesstimate our way to the first one).
 */
function estimateFirstTimecode(element, sourceName) {
  const sources = getTimecodeSources(element);
  if (!sources) {
    return undefined;
  }

  let source = sources.find(s => s.track === sourceName);
  if (!source) {
    source = sources[sources.length - 1];
  }

  const frames = Number(source.frame);
  const framerate = Number(source.value.split('@')[1]);
  const isDropframe = source.value.includes(';');

  const timecodeIfZeroBased = ToTimeCode(frames, framerate, isDropframe);
  const offsetTimecode = SubtractTimecodes(source.value, timecodeIfZeroBased);

  return offsetTimecode;
}

/**
 * Gets the last timecode contained in the special metadata tracks of the specified
 * video element. Note that since the exact timecode can only be retrieved if the
 * end of the video is buffered, this operation just makes an estimate based on the
 * offset from the first timecode and the duration of the clip.
 *
 * @param {HTMLVideoElement} element The element from which to extract the timecode.
 * @returns The last timecode contained in the specified element.
 */
export function getLastTimecode(element, sourceName) {
  const firstTimecode = getFirstTimecode(element, sourceName);
  if (!firstTimecode) {
    return undefined;
  }

  const framerate = TimecodeService.getFrameRateFromGlimTimecode(element);
  const frames = Math.round(element.duration * framerate);

  const estimatedTimecode = ToTimeCode(frames, framerate, firstTimecode.includes(';'));
  return AddTimecodes(estimatedTimecode, firstTimecode);
}

/**
 * Get the timecode at the specified time. If the time specified references a point
 * in the clip that is buffered, the exact timecode can be determined. If that point
 * is not buffered, an estimate will be made based on the offset from the first
 * timecode and the duration of the clip.
 *
 * @param {HTMLVideoElement} element The element from which to extract the timecode.
 * @param {number} time The time for which to retrieve the timecode.
 * @param {string} sourceName Optionally, the name of the timecode track to use.
 * @returns The timecode at the specified time.
 */
export function getTimecode(element, time, sourceName) {
  const sources = getTimecodeSources(element, time + EPSILON);

  // Exact:
  if (sources) {
    let source = sources.find(s => s.track === sourceName);
    if (!source) {
      source = sources[sources.length - 1];
    }

    return source.value;
  }

  // Estimate:
  else {
    const firstTimecode = getFirstTimecode(element, sourceName);
    if (!firstTimecode) {
      return undefined;
    }

    const framerate = TimecodeService.getFrameRateFromGlimTimecode(element);
    const frames = Math.floor(time * framerate);
    const isDropframe = firstTimecode.includes(';');

    const estimatedTimecode = ToTimeCode(frames, framerate, isDropframe);
    return AddTimecodes(estimatedTimecode, firstTimecode);
  }
}

function getCompositionTime(timecodeCue) {
  for (const item of timecodeCue.value.info) {
    if (item.name == "compositionTime") {
      return item.value;
    }
  }
  return '';
}

export function zeroPad(num, places) {
  return String(num).padStart(places, '0');
}

export function nptToTimecodeSeconds(npt) {
  const elements = npt.split('@');
  if (elements.length !== 2) {
    return undefined;
  }
  let time = Number(elements[0]);
  const framerate = Number(elements[1]);

  const hours = Math.floor(time / 3600);
  time -= hours * 3600;
  const minutes = Math.floor(time / 60);
  time -= minutes * 60;
  const seconds = Math.floor(time);
  time -= seconds;
  const remainder = Math.floor(time * 1000);

  const code = zeroPad(hours, 2) + ':' +
    zeroPad(minutes, 2) + ':' +
    zeroPad(seconds, 2) + '.' +
    zeroPad(remainder, 3) + '@' + framerate;
  return code;
}

export function nptToTimecode(npt) {
  const elements = npt.split('@');
  if (elements.length !== 2) {
    return undefined;
  }
  let time = Number(elements[0]);
  const framerate = Number(elements[1]);

  const hours = Math.floor(time / 3600);
  time -= hours * 3600;
  const minutes = Math.floor(time / 60);
  time -= minutes * 60;
  const seconds = Math.floor(time);
  time -= seconds;
  const frame = Math.floor(time * framerate);

  const code = zeroPad(hours, 2) + ':' +
    zeroPad(minutes, 2) + ':' +
    zeroPad(seconds, 2) + ':' +
    zeroPad(frame, 2) + '@' + framerate;
  return code;
}


export function nptToTime(npt) {
  const elements = npt.split('@');
  if (elements.length !== 2) {
    return undefined;
  }
  const seconds = Number(elements[0]);
  return seconds;
}

export function getTimecodeSources(element, time) {
  const timecodeCue = TimecodeService.getCurrentTimecodeCue(element, time);
  if (!timecodeCue || !timecodeCue.value.info) {
    return;
  }

  const timecodeData = TimecodeService.extractDataFromTimecodeCue(timecodeCue);

  let sources = [];
  TimecodeService.findAndSetTimecodes(timecodeData.metadata, sources);

  sources.push({
    track: GLIM_TIMECODE_SOURCE.name,
    value: timecodeData.glimpseTimecode.value,
    frame: timecodeData.frame
  });

  sources.push({
    track: NPT_TIMECODE_SOURCE.name,
    value: getCompositionTime(timecodeCue),
    frame: timecodeData.frame
  });

  const framerate = timecodeData.glimpseTimecode.value.split('@')[1];
  const frame = timecodeData.frame;

  sources.forEach(s => {
    if (!s.value.includes('@')) {
      s.value = `${s.value}@${framerate}`;
    }

    if (!s.frame) {
      s.frame = frame;
    }
  });

  return sources;
}